import { defineStore, acceptHMRUpdate } from 'pinia';
import { ref, computed } from 'vue';
import { signIn, refreshSession, logout, getCurrentUser } from '@/services/userService';
import router from '@/router';
import {
    canAccessModule,
    resolveAllowedModules,
    type AuthUser,
    type ModuleKey,
    type RoleName,
} from '@/permissions';
import { useSuperAdminCreditorAccessStore } from '@/stores/superAdminCreditorAccess';

export const useAuthStore = defineStore('auth', () => {

    // STATE
    const user = ref<AuthUser | null>(null);
    const isRefreshing = ref(false);

    // GETTERS
    const isAuthenticated = computed(() => !!user.value);
    const roleName = computed<RoleName>(() => user.value?.role_name ?? null);
    const allowedModules = computed<ModuleKey[]>(() => resolveAllowedModules(roleName.value));
    const hasAnyModuleAccess = computed(() => allowedModules.value.length > 0);

    
    //Returns false for unauthenticated users and for unknown / null roles.
    const can = (module: ModuleKey): boolean => {
        if (!isAuthenticated.value) return false;
        return canAccessModule(roleName.value, module);
    };

    const syncSuperAdminImpersonation = async () => {
        const creditorAccessStore = useSuperAdminCreditorAccessStore();

        if (user.value?.role_name === 'superadmin') {
            await creditorAccessStore.hydrateAccess();
            return;
        }

        creditorAccessStore.clearAccess();
    };

    // ACTIONS
    const signInUser = async (code: string, codeVerifier: string) => {
        try {
            await signIn(code, codeVerifier);
            const response = await getCurrentUser();
            user.value = response.user;
            localStorage.removeItem('creditor_portal_code_verifier');
            await syncSuperAdminImpersonation();
            if (user.value?.role_name === 'superadmin') {
                router.push({ name: 'superadmin-creditors' });
            } else {
                router.push({ name: 'dashboard' });
            }
        } catch (error: any) {
            throw error;
        }
    };

    const refreshUserSession = async () => {
        if (isRefreshing.value) return;

        try {
            isRefreshing.value = true;
            await refreshSession();
            const response = await getCurrentUser();
            user.value = response.user;
            await syncSuperAdminImpersonation();
        } catch (error) {
            console.error(error);
        } finally {
            isRefreshing.value = false;
        }
    };

    const clearSession = (redirect: boolean = true) => {
        user.value = null;
        useSuperAdminCreditorAccessStore().clearAccess();
        if (redirect) {
            router.push({ name: 'sign-in' });
        }
    };

    const logoutUser = async (withRedirect: boolean = true) => {
        try {
            await logout(withRedirect);
        } catch (error) {
            console.error(error);
        } finally {
            clearSession(false);
        }
    };

    return {
        user,
        isRefreshing,
        isAuthenticated,
        roleName,
        allowedModules,
        hasAnyModuleAccess,
        can,
        signInUser,
        refreshUserSession,
        clearSession,
        logoutUser,
    };
});

if (module.hot) {
    module.hot.accept(acceptHMRUpdate(useAuthStore, module.hot));
}
